<script setup lang="ts">
import { computed } from 'vue';

import BxTabs, { type TabItem } from '@/components/common/BxTabs.vue';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { ToolTip } from '@/helpers/tippy';

import type { SectionStateType, SectionStates } from '@/types/SectionState';

const props = withDefaults(
  defineProps<{
    showError?: boolean;
    sectionKey?: Array<keyof SectionStates> | keyof SectionStates | null;
    baseTestId?: string;
  }>(),
  { baseTestId: 'simple-comprehensive', sectionKey: null },
);

interface TabExtra {
  tooltip: string;
}

const tabsData: TabItem<string, TabExtra>[] = [
  {
    title: useFeatureFlags().isCommercialQuoteFlowEnabled() ? 'Quick' : 'Simple',
    value: 'simple',
    tooltip: 'Fewer questions to complete your scenario quickly, for an easy start and initial estimate.',
  },
  {
    title: 'Comprehensive',
    value: 'comprehensive',
    tooltip:
      'More detail upfront for a clearer picture of your client’s scenario, helping us provide a more accurate quote.',
  },
];

const { sectionStates, startStateType, updateStartStateType, updateSpecificSectionState } = useUserQuoteProcess();

const selectedValue = computed(() => {
  if (!props.sectionKey) {
    return startStateType.value;
  }

  if (props.sectionKey instanceof Array) {
    return sectionStates.value[props.sectionKey[0] as keyof SectionStates];
  }

  return sectionStates.value[props.sectionKey];
});

const selectDetailType = (detail: SectionStateType) => {
  if (props.sectionKey instanceof Array) {
    props.sectionKey.forEach((key) => {
      updateSpecificSectionState(key, detail);
    });
  } else if (props.sectionKey && typeof props.sectionKey === 'string') {
    updateSpecificSectionState(props.sectionKey, detail);
  } else {
    // trigger this only in StartStep.vue
    updateStartStateType(detail);
  }
};
</script>

<template>
  <div class="w-[350px] max-w-md px-2 sm:w-[400px] sm:px-0">
    <BxTabs
      :model-value="selectedValue"
      :tabs="tabsData"
      :show-error="showError"
      @update:model-value="selectDetailType($event as SectionStateType)"
    >
      <template #tab="{ tab, selected }">
        <div class="w-full focus:outline-none">
          <ToolTip :options="{ placement: 'bottom', delay: 100, touch: false }">
            <button
              :data-test-id="`${props.baseTestId}-tab-${tab.value}`"
              class="text-md w-full rounded-lg py-2.5 font-medium leading-5 ring-white/60 ring-offset-2 focus:outline-none"
              :class="[
                selected && selectedValue !== null
                  ? 'bg-bridgit-vibrantGreen text-bridgit-primaryBtnText drop-shadow-md'
                  : 'text-bridgit-primaryBtnText focus:ring-2',
              ]"
            >
              {{ tab.title }}
            </button>
            <template #content>
              {{ tab.tooltip }}
            </template>
          </ToolTip>
        </div>
      </template>
    </BxTabs>
  </div>
</template>
